import { CSSProperties, ReactNode, forwardRef, useMemo } from 'react';

import { NumberKeyboard } from 'antd-mobile';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useFormErrorContext } from '../Form/FormError';

import { InputWrapper, VirtualInputCustom } from './Input.styled';

export interface InputVirtualNumberProps {
  value?: string;
  /**
   * 숫자 앞에 표시되는 단위
   */
  prefix?: ReactNode;
  /**
   * 숫자 뒤에 표시되는 단위
   */
  suffix?: ReactNode;
  /**
   * 가로 넓이가 공간을 전부 채우도록 합니다.
   */
  block?: boolean;
  /**
   * 상태
   *
   * validation에 대한 피드백으로 활용하세요
   */
  status?: 'error' | 'warning';
  /**
   * clear 버튼 노출
   */
  allowClear?: boolean;
  disabled?: boolean;
  customKey?: string | [string, string];
  style?: Partial<CSSProperties>;
  onChange?: (value?: string) => void;
}

export const InputVirtualNumber = forwardRef<HTMLInputElement, InputVirtualNumberProps>(
  (props, ref) => {
    const {
      value,
      status,
      prefix,
      suffix,
      block,
      style,
      allowClear,
      customKey,
      disabled,
      onChange,
    } = props;
    const { t } = useTranslation('global');
    const { error } = useFormErrorContext();

    const isError = useMemo(() => {
      if (error) return true;
      return status === 'error';
    }, [status, error]);

    return (
      <InputWrapper className={clsx({ disabled, block, error: isError })} style={style}>
        {prefix && <UnitSpan>{prefix}</UnitSpan>}
        <VirtualInputCustom
          ref={ref}
          value={value}
          clearable={allowClear}
          onChange={onChange}
          disabled={disabled}
          keyboard={
            <NumberKeyboard
              destroyOnClose
              customKey={customKey}
              confirmText={t('global:action_type_confirm')}
              onConfirm={() => {
                onChange?.(value);
              }}
            />
          }
          style={{
            width: '100%',
            ...style,
          }}
        />
        {suffix && <UnitSpan>{suffix}</UnitSpan>}
      </InputWrapper>
    );
  },
);

const UnitSpan = styled.span`
  flex: 0 0 auto;
  display: inline-block;
  color: #7f8892;
`;
